import { useState, useEffect, useMemo } from "react";
import { createOrder, listMasterSkus, subscribeMasterSkus, updateMasterSkuStock } from "../lib/dataStore";
import { calculateCodCharge, calculateItemTax, INDIAN_STATES, isWestBengalState } from "../lib/orderHelpers";
import { buildWhatsAppOrderUrl } from "../lib/whatsapp";
import { toast } from "sonner";
import {
  X, Plus, Trash2, Store, ShoppingCart, Package, Edit3, Banknote,
  CreditCard, Send, CheckCircle2, Boxes, Search, Copy, Check, AlertCircle, Sparkles, FileText
} from "lucide-react";
import { parseComboQty } from "../lib/productHelpers";

const DEFAULT_STORE_ADDRESS = process.env.REACT_APP_COMPANY_ADDRESS || "Santiniketan Studio, Bolpur";
const DEFAULT_STORE_CITY = process.env.REACT_APP_COMPANY_CITY || "Bolpur";
const DEFAULT_STORE_STATE = process.env.REACT_APP_COMPANY_STATE || "West Bengal";
const DEFAULT_STORE_PINCODE = process.env.REACT_APP_COMPANY_PINCODE || "731204";

export default function OfflineOrderModal({
  products = [],
  masterSkus: propMasterSkus = [],
  initialSkuItem = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState({
    customerName: "Walk-in Customer",
    phone: "",
    email: "",
    isCounterSale: true,
    address: DEFAULT_STORE_ADDRESS,
    city: DEFAULT_STORE_CITY,
    state: DEFAULT_STORE_STATE,
    pincode: DEFAULT_STORE_PINCODE,
    notes: "In-store sale / payment collected",
    status: "New",
    discount: "0",
    shippingCharge: "0",
    extraCharge: "0",
    deliveryIncluded: true,
    customerGst: "",
    breakdownCgstSgst: false,
  });

  const [isGstOrder, setIsGstOrder] = useState(true); // true = GST Invoice, false = Non-GST Invoice
  const [gstMode, setGstMode] = useState("inclusive"); // "inclusive" | "exclusive" | "non-gst"
  const [manualHsn, setManualHsn] = useState("4202");
  const [masterSkus, setMasterSkus] = useState(propMasterSkus || []);

  useEffect(() => {
    if (propMasterSkus && propMasterSkus.length > 0) {
      setMasterSkus(propMasterSkus);
      return;
    }
    const unsub = subscribeMasterSkus((list) => {
      if (list && list.length > 0) setMasterSkus(list);
    });
    return () => unsub && unsub();
  }, [propMasterSkus]);

  const availableProducts = useMemo(() => {
    return products.filter((p) => p.variants && p.variants.some((v) => Number(v.stock || 0) > 0));
  }, [products]);

  // Item source mode: "sku" | "catalog" | "manual"
  const [itemSourceMode, setItemSourceMode] = useState(
    initialSkuItem || (propMasterSkus && propMasterSkus.length > 0) ? "sku" : "catalog"
  );

  // Master SKU item fields
  const [skuSearch, setSkuSearch] = useState("");
  const [selectedSkuId, setSelectedSkuId] = useState(initialSkuItem?.id || "");
  const [skuPrice, setSkuPrice] = useState(initialSkuItem?.saleCost || 0);
  const [skuTaxRate, setSkuTaxRate] = useState(String(initialSkuItem?.gstRate ?? "0"));
  const [skuQuantity, setSkuQuantity] = useState(1);

  // Filtered Master SKUs by search query
  const filteredMasterSkus = useMemo(() => {
    if (!skuSearch.trim()) return masterSkus;
    const q = skuSearch.trim().toLowerCase();
    return masterSkus.filter((s) =>
      (s.sku || "").toLowerCase().includes(q) ||
      (s.name || "").toLowerCase().includes(q) ||
      (s.category || "").toLowerCase().includes(q) ||
      (s.material || "").toLowerCase().includes(q) ||
      (s.colour || "").toLowerCase().includes(q) ||
      (s.barcode || "").toLowerCase().includes(q)
    );
  }, [masterSkus, skuSearch]);

  const currentMasterSku = useMemo(() => {
    if (selectedSkuId) {
      const match = masterSkus.find((s) => s.id === selectedSkuId || s.sku === selectedSkuId);
      if (match) return match;
    }
    return filteredMasterSkus[0] || masterSkus[0] || null;
  }, [masterSkus, selectedSkuId, filteredMasterSkus]);

  useEffect(() => {
    if (initialSkuItem) {
      setSelectedSkuId(initialSkuItem.id);
      setSkuPrice(initialSkuItem.saleCost || 0);
      setSkuTaxRate(String(initialSkuItem.gstRate ?? "0"));
      setItemSourceMode("sku");
    } else if (masterSkus.length > 0 && !selectedSkuId) {
      setSelectedSkuId(masterSkus[0].id);
      setSkuPrice(masterSkus[0].saleCost || 0);
      setSkuTaxRate(String(masterSkus[0].gstRate ?? "0"));
    }
  }, [initialSkuItem, masterSkus, selectedSkuId]);

  useEffect(() => {
    if (currentMasterSku) {
      setSkuPrice(currentMasterSku.saleCost != null ? Number(currentMasterSku.saleCost) : 0);
      setSkuTaxRate(String(currentMasterSku.gstRate ?? "0"));
    }
  }, [currentMasterSku]);

  // Catalog item fields
  const [productId, setProductId] = useState(availableProducts[0]?.id || products[0]?.id || "");
  const currentProduct = useMemo(() => {
    return products.find((p) => p.id === productId) || products[0];
  }, [products, productId]);

  const [variantTag, setVariantTag] = useState(currentProduct?.variants?.[0]?.tag || "Default");
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(currentProduct?.price || 0);
  const [taxRate, setTaxRate] = useState("0");

  // Manual item fields
  const [manualName, setManualName] = useState("");
  const [manualVariant, setManualVariant] = useState("Custom");
  const [manualPrice, setManualPrice] = useState("0");
  const [manualTaxRate, setManualTaxRate] = useState("0");
  const [manualQuantity, setManualQuantity] = useState(1);

  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  // Payment mode: "prepaid" | "cod"
  const [paymentMode, setPaymentMode] = useState("prepaid");
  const [customCodCharge, setCustomCodCharge] = useState("");
  const [isCustomCod, setIsCustomCod] = useState(false);
  const [codAdvancePaid, setCodAdvancePaid] = useState("0");
  const [sendWhatsApp, setSendWhatsApp] = useState(false);

  useEffect(() => {
    if (currentProduct) {
      const v0 = currentProduct.variants?.[0];
      setVariantTag(v0?.tag || "Default");
      setUnitPrice(v0?.price != null && v0?.price !== "" ? Number(v0.price) : Number(currentProduct.price || 0));
      const defaultRate = currentProduct.gstRate != null && currentProduct.gstRate !== "" && Number(currentProduct.gstRate) > 0
        ? String(currentProduct.gstRate)
        : (isGstOrder ? "5" : "0");
      setTaxRate(defaultRate);
      setQuantity(1);
    }
  }, [productId, currentProduct, isGstOrder]);

  useEffect(() => {
    if (currentProduct && variantTag) {
      const v = (currentProduct.variants || []).find((v) => v.tag === variantTag);
      if (v && v.price != null && v.price !== "") {
        setUnitPrice(Number(v.price));
      } else if (currentProduct.price != null) {
        setUnitPrice(Number(currentProduct.price));
      }
    }
  }, [variantTag, currentProduct]);

  const selectedVariantStock = useMemo(() => {
    if (!currentProduct) return 0;
    const v = (currentProduct.variants || []).find((v) => v.tag === variantTag);
    return Number(v?.stock || 0);
  }, [currentProduct, variantTag]);

  const isGstInclusive = isGstOrder && gstMode === "inclusive";

  // Pure helper to calculate taxable base, GST tax amount, and line total for any item
  const calculateLineGst = (price, qty, taxRate, isInc) => {
    const numPrice = Math.max(0, Number(price || 0));
    const numQty = Math.max(1, Number(qty || 1));
    const numRate = isGstOrder ? Math.max(0, Number(taxRate || 0)) : 0;

    if (numRate === 0 || !isGstOrder) {
      const lineTotal = Number((numPrice * numQty).toFixed(2));
      return {
        baseUnitPrice: numPrice,
        unitTax: 0,
        lineBase: lineTotal,
        taxAmount: 0,
        lineTotal,
      };
    }

    if (isInc) {
      // price includes GST (Retail MRP)
      const baseUnitPrice = Number((numPrice / (1 + numRate / 100)).toFixed(2));
      const unitTax = Number((numPrice - baseUnitPrice).toFixed(2));
      const lineTotal = Number((numPrice * numQty).toFixed(2));
      const lineBase = Number((baseUnitPrice * numQty).toFixed(2));
      const taxAmount = Number((lineTotal - lineBase).toFixed(2));
      return {
        baseUnitPrice,
        unitTax,
        lineBase,
        taxAmount,
        lineTotal,
      };
    } else {
      // price is base taxable price excluding GST
      const baseUnitPrice = numPrice;
      const unitTax = Number(((numPrice * numRate) / 100).toFixed(2));
      const lineBase = Number((numPrice * numQty).toFixed(2));
      const taxAmount = Number(((lineBase * numRate) / 100).toFixed(2));
      const lineTotal = Number((lineBase + taxAmount).toFixed(2));
      return {
        baseUnitPrice,
        unitTax,
        lineBase,
        taxAmount,
        lineTotal,
      };
    }
  };

  // Live item previews for instant visual feedback
  const skuPreview = useMemo(() => {
    return calculateLineGst(skuPrice, skuQuantity, skuTaxRate, isGstInclusive);
  }, [skuPrice, skuQuantity, skuTaxRate, isGstInclusive, isGstOrder]);

  const catalogPreview = useMemo(() => {
    return calculateLineGst(unitPrice, quantity, taxRate, isGstInclusive);
  }, [unitPrice, quantity, taxRate, isGstInclusive, isGstOrder]);

  const manualPreview = useMemo(() => {
    return calculateLineGst(manualPrice, manualQuantity, manualTaxRate, isGstInclusive);
  }, [manualPrice, manualQuantity, manualTaxRate, isGstInclusive, isGstOrder]);

  // Handle switching GST vs Non-GST order
  const handleToggleGstOrder = (enabled) => {
    setIsGstOrder(enabled);
    if (!enabled) {
      setGstMode("non-gst");
      const updated = items.map((it) => {
        const lineTotal = Number((Number(it.price || 0) * Number(it.quantity || 1)).toFixed(2));
        return {
          ...it,
          baseUnitPrice: Number(it.price || 0),
          lineBase: lineTotal,
          taxAmount: 0,
          total: lineTotal,
          priceIncludesGst: false,
        };
      });
      setItems(updated);
      toast.info("Switched to Non-GST Invoice (Excluded from GST reports & no tax on invoice)");
    } else {
      setGstMode("inclusive");
      const updated = items.map((it) => {
        const rate = Number(it.taxRate != null ? it.taxRate : (it.gstRate || 0));
        const calc = calculateLineGst(it.price, it.quantity, rate, true);
        return {
          ...it,
          baseUnitPrice: calc.baseUnitPrice,
          lineBase: calc.lineBase,
          taxAmount: calc.taxAmount,
          total: calc.lineTotal,
          priceIncludesGst: true,
        };
      });
      setItems(updated);
      toast.info("Switched to GST Tax Invoice");
    }
  };

  // Handle switching GST mode (Include GST vs Exclude GST)
  const handleGstModeChange = (newMode) => {
    if (newMode === gstMode) return;
    setGstMode(newMode);
    const isInc = newMode === "inclusive";
    if (items.length > 0) {
      const updated = items.map((it) => {
        const rate = Number(it.taxRate != null ? it.taxRate : (it.gstRate || 0));
        const calc = calculateLineGst(it.price, it.quantity, rate, isInc);
        return {
          ...it,
          baseUnitPrice: calc.baseUnitPrice,
          lineBase: calc.lineBase,
          taxAmount: calc.taxAmount,
          total: calc.lineTotal,
          priceIncludesGst: isInc,
        };
      });
      setItems(updated);
      toast.info(
        isInc
          ? "Switched to Include GST mode (Prices include GST)"
          : "Switched to Exclude GST mode (GST added on top of prices)"
      );
    }
  };

  const addSkuItem = () => {
    if (!currentMasterSku) return toast.error("Select a Master SKU");
    const numQty = Number(skuQuantity);
    if (!numQty || numQty <= 0) return toast.error("Quantity must be at least 1");

    const currentStock = Number(currentMasterSku.quantity || 0);
    if (currentStock === 0) {
      const ok = window.confirm(
        `Warning: SKU "${currentMasterSku.sku}" currently has 0 units in stock. Do you still want to add this item?`
      );
      if (!ok) return;
    } else if (numQty > currentStock) {
      toast.warning(`Note: Adding ${numQty} units exceeds current stock (${currentStock} in stock).`);
    }

    const numPrice = Number(skuPrice || 0);
    const numTaxRate = isGstOrder ? Math.max(0, Number(skuTaxRate || 0)) : 0;
    const skuHsn = currentMasterSku.hsn || currentMasterSku.hsnCode || "4202";
    const calc = calculateLineGst(numPrice, numQty, numTaxRate, isGstInclusive);

    const existingIdx = items.findIndex(
      (i) => (i.sku && i.sku === currentMasterSku.sku) || (i.id === currentMasterSku.id && !i.isManual)
    );
    if (existingIdx >= 0) {
      const copy = [...items];
      const newQty = copy[existingIdx].quantity + numQty;
      const rec = calculateLineGst(numPrice, newQty, numTaxRate, isGstInclusive);
      copy[existingIdx] = {
        ...copy[existingIdx],
        quantity: newQty,
        price: numPrice,
        hsn: skuHsn,
        hsnCode: skuHsn,
        taxRate: numTaxRate,
        gstRate: numTaxRate,
        baseUnitPrice: rec.baseUnitPrice,
        lineBase: rec.lineBase,
        taxAmount: rec.taxAmount,
        total: rec.lineTotal,
        priceIncludesGst: isGstInclusive,
      };
      setItems(copy);
    } else {
      setItems([
        ...items,
        {
          id: currentMasterSku.id,
          sku: currentMasterSku.sku,
          name: currentMasterSku.name,
          variantTag: currentMasterSku.sku,
          hsn: skuHsn,
          hsnCode: skuHsn,
          price: numPrice,
          baseUnitPrice: calc.baseUnitPrice,
          lineBase: calc.lineBase,
          purchaseCost: Number(currentMasterSku.purchaseCost || 0),
          additionalCost: Number(currentMasterSku.additionalCost || 0),
          taxRate: numTaxRate,
          gstRate: numTaxRate,
          taxAmount: calc.taxAmount,
          total: calc.lineTotal,
          quantity: numQty,
          shippingCharge: Number(currentMasterSku.shippingCharge || 0),
          isMasterSku: true,
          priceIncludesGst: isGstInclusive,
        },
      ]);
    }
    toast.success(`Added SKU ${currentMasterSku.sku} to invoice`);
  };

  const addItem = () => {
    if (!currentProduct) return toast.error("Select a product");
    if (!quantity || quantity <= 0) return toast.error("Quantity must be at least 1");
    if (quantity > selectedVariantStock) {
      toast.error(`Only ${selectedVariantStock} units available in stock for ${variantTag}`);
      return;
    }

    const numPrice = Number(unitPrice || 0);
    const numQty = Number(quantity);
    const numTaxRate = isGstOrder ? Math.max(0, Number(taxRate || 0)) : 0;
    const prodHsn = currentProduct.hsn || currentProduct.hsnCode || "4202";
    const calc = calculateLineGst(numPrice, numQty, numTaxRate, isGstInclusive);

    const existingIdx = items.findIndex((i) => i.id === currentProduct.id && i.variantTag === variantTag && !i.isManual);
    if (existingIdx >= 0) {
      const copy = [...items];
      const newQty = copy[existingIdx].quantity + numQty;
      if (newQty > selectedVariantStock) {
        toast.error(`Cannot add more than total stock (${selectedVariantStock})`);
        return;
      }
      const rec = calculateLineGst(numPrice, newQty, numTaxRate, isGstInclusive);
      copy[existingIdx] = {
        ...copy[existingIdx],
        quantity: newQty,
        price: numPrice,
        hsn: prodHsn,
        hsnCode: prodHsn,
        taxRate: numTaxRate,
        gstRate: numTaxRate,
        baseUnitPrice: rec.baseUnitPrice,
        lineBase: rec.lineBase,
        taxAmount: rec.taxAmount,
        total: rec.lineTotal,
        priceIncludesGst: isGstInclusive,
      };
      setItems(copy);
    } else {
      const vObj = (currentProduct.variants || []).find((v) => v.tag === variantTag);
      const comboQty = parseComboQty(vObj?.comboQty);
      setItems([
        ...items,
        {
          id: currentProduct.id,
          name: currentProduct.name,
          variantTag: variantTag || "Default",
          hsn: prodHsn,
          hsnCode: prodHsn,
          price: numPrice,
          baseUnitPrice: calc.baseUnitPrice,
          lineBase: calc.lineBase,
          taxRate: numTaxRate,
          gstRate: numTaxRate,
          taxAmount: calc.taxAmount,
          total: calc.lineTotal,
          quantity: numQty,
          shippingCharge: Number(currentProduct.shippingCharge || 0),
          comboQty,
          isManual: false,
          priceIncludesGst: isGstInclusive,
        },
      ]);
    }
    toast.success(`Added ${currentProduct.name} (${variantTag})`);
  };

  const addManualItem = () => {
    if (!manualName.trim()) {
      return toast.error("Please enter item name");
    }
    const numPrice = Number(manualPrice);
    if (isNaN(numPrice) || numPrice < 0) {
      return toast.error("Please enter a valid rate / price");
    }
    const numQty = Number(manualQuantity);
    if (!numQty || numQty <= 0) {
      return toast.error("Quantity must be at least 1");
    }
    const numTaxRate = isGstOrder ? Math.max(0, Number(manualTaxRate || 0)) : 0;
    const customHsn = (manualHsn || "").trim() || "4202";
    const calc = calculateLineGst(numPrice, numQty, numTaxRate, isGstInclusive);

    const manualId = "manual_" + Date.now() + "_" + Math.floor(Math.random() * 1000);
    const newItem = {
      id: manualId,
      name: manualName.trim(),
      variantTag: manualVariant.trim() || "Custom",
      hsn: customHsn,
      hsnCode: customHsn,
      price: numPrice,
      baseUnitPrice: calc.baseUnitPrice,
      lineBase: calc.lineBase,
      taxRate: numTaxRate,
      gstRate: numTaxRate,
      taxAmount: calc.taxAmount,
      total: calc.lineTotal,
      quantity: numQty,
      isManual: true,
      priceIncludesGst: isGstInclusive,
    };

    setItems([...items, newItem]);
    toast.success(`Added custom item: ${manualName.trim()}`);

    // Reset manual form inputs
    setManualName("");
    setManualVariant("Custom");
    setManualPrice("0");
    setManualTaxRate("0");
    setManualQuantity(1);
  };

  const removeItem = (idx) => {
    setItems(items.filter((_, i) => i !== idx));
  };

  const updateItemQty = (idx, qty) => {
    const copy = [...items];
    const item = copy[idx];
    let validQty = 1;
    if (item.isManual || String(item.id).startsWith("manual_")) {
      validQty = Math.max(1, Number(qty || 1));
    } else {
      const p = products.find((x) => x.id === item.id);
      const v = p?.variants?.find((x) => x.tag === item.variantTag);
      const maxStock = Number(v?.stock || 999);
      validQty = Math.max(1, Math.min(maxStock, Number(qty || 1)));
    }
    const rate = Number(item.taxRate != null ? item.taxRate : (item.gstRate || 0));
    const calc = calculateLineGst(item.price, validQty, rate, isGstInclusive);
    copy[idx] = {
      ...item,
      quantity: validQty,
      taxRate: rate,
      gstRate: rate,
      baseUnitPrice: calc.baseUnitPrice,
      lineBase: calc.lineBase,
      taxAmount: calc.taxAmount,
      total: calc.lineTotal,
      priceIncludesGst: isGstInclusive,
    };
    setItems(copy);
  };

  const updateItemTaxRate = (idx, newRate) => {
    const copy = [...items];
    const item = copy[idx];
    const rate = Math.max(0, Number(newRate || 0));
    const calc = calculateLineGst(item.price, item.quantity, rate, isGstInclusive);
    copy[idx] = {
      ...item,
      taxRate: rate,
      gstRate: rate,
      baseUnitPrice: calc.baseUnitPrice,
      lineBase: calc.lineBase,
      taxAmount: calc.taxAmount,
      total: calc.lineTotal,
      priceIncludesGst: isGstInclusive,
    };
    setItems(copy);
  };

  const updateItemPrice = (idx, newPrice) => {
    const copy = [...items];
    const item = copy[idx];
    const price = Math.max(0, Number(newPrice || 0));
    const rate = Number(item.taxRate != null ? item.taxRate : (item.gstRate || 0));
    const calc = calculateLineGst(price, item.quantity, rate, isGstInclusive);
    copy[idx] = {
      ...item,
      price,
      baseUnitPrice: calc.baseUnitPrice,
      lineBase: calc.lineBase,
      taxAmount: calc.taxAmount,
      total: calc.lineTotal,
      priceIncludesGst: isGstInclusive,
    };
    setItems(copy);
  };

  const shippingVal = Math.max(0, Number(form.shippingCharge || 0));
  const extraVal = Math.max(0, Number(form.extraCharge || 0));

  // Taxable subtotal (base excluding GST)
  const taxableSubtotal = items.reduce((s, i) => {
    const rate = Number(i.taxRate != null ? i.taxRate : (i.gstRate || 0));
    const calc = calculateLineGst(i.price, i.quantity, rate, isGstInclusive);
    return s + calc.lineBase;
  }, 0);

  // Total tax amount
  const totalTax = items.reduce((s, i) => {
    const rate = Number(i.taxRate != null ? i.taxRate : (i.gstRate || 0));
    const calc = calculateLineGst(i.price, i.quantity, rate, isGstInclusive);
    return s + calc.taxAmount;
  }, 0);
  const roundedTax = Number(totalTax.toFixed(2));

  // Gross items total
  const grossItemsTotal = isGstInclusive
    ? items.reduce((s, i) => s + (Number(i.price || 0) * Number(i.quantity || 1)), 0)
    : (taxableSubtotal + roundedTax);

  const discountVal = Math.max(0, Number(form.discount || 0));
  const effectiveShipping = form.deliveryIncluded !== false ? 0 : shippingVal;

  // Explicit subtotal to prevent any ReferenceError: subtotal is not defined
  const subtotal = isGstOrder ? Number(taxableSubtotal.toFixed(2)) : grossItemsTotal;

  // Total bill before COD handling fee
  const totalBillBeforeCod = Math.max(
    0,
    grossItemsTotal + effectiveShipping + extraVal - discountVal
  );

  // COD Handling Calculation: ₹50 or 3% of total bill, whichever is higher
  const autoCodCharge = calculateCodCharge(totalBillBeforeCod);
  const effectiveCodCharge = paymentMode === "cod"
    ? (isCustomCod && customCodCharge !== "" ? Math.max(0, Number(customCodCharge)) : autoCodCharge)
    : 0;

  // Grand Total: Total bill + COD handling cost
  const grandTotal = Math.max(0, Math.round(totalBillBeforeCod + effectiveCodCharge));

  // Advance Amount: Manually filled for manual invoice generation
  const numAdvancePaid = paymentMode === "cod" ? Math.max(0, Number(codAdvancePaid || 0)) : 0;
  const advanceAmount = Math.min(grandTotal, numAdvancePaid);

  // Collect on Delivery: (Total Bill + COD handling cost) - Advance Amount
  const balanceDue = paymentMode === "cod" ? Math.max(0, grandTotal - advanceAmount) : 0;

  const toggleCounterSale = (isCounter) => {
    if (isCounter) {
      setForm((f) => ({
        ...f,
        isCounterSale: true,
        address: DEFAULT_STORE_ADDRESS,
        city: DEFAULT_STORE_CITY,
        state: DEFAULT_STORE_STATE,
        pincode: DEFAULT_STORE_PINCODE,
      }));
    } else {
      setForm((f) => ({
        ...f,
        isCounterSale: false,
        address: "",
        city: "",
        state: "",
        pincode: "",
      }));
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Add at least one item to the order");
    if (!form.customerName.trim()) return toast.error("Customer name is required");
    if (!form.phone.trim()) return toast.error("Phone number is required");
    if (!form.address.trim() || !form.city.trim() || !form.pincode.trim()) {
      return toast.error("Address details are required");
    }

    setBusy(true);
    try {
      const isCod = paymentMode === "cod";
      const isIntra = isWestBengalState(form.state);
      const appliedCgst = isGstOrder && isIntra ? Number((roundedTax / 2).toFixed(2)) : 0;
      const appliedSgst = isGstOrder && isIntra ? Number((roundedTax - appliedCgst).toFixed(2)) : 0;
      const appliedIgst = isGstOrder && !isIntra ? roundedTax : 0;

      const order = {
        orderMode: "Offline",
        isGst: isGstOrder,
        isGstOrder: isGstOrder,
        isNonGst: !isGstOrder,
        nonGst: !isGstOrder,
        gstType: isGstOrder ? "gst" : "non-gst",
        gstMode: isGstOrder ? gstMode : "none",
        priceIncludesGst: isGstOrder && isGstInclusive,
        breakdownCgstSgst: isGstOrder && isIntra,
        customerName: form.customerName.trim(),
        customerGst: isGstOrder ? (form.customerGst || "").trim().toUpperCase() : "",
        gstin: isGstOrder ? (form.customerGst || "").trim().toUpperCase() : "",
        phone: form.phone.trim(),
        email: form.email.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        state: (form.state || "West Bengal").trim(),
        isIntraState: isIntra,
        cgst: appliedCgst,
        sgst: appliedSgst,
        igst: appliedIgst,
        pincode: form.pincode.trim(),
        notes: form.notes.trim(),
        status: form.status,
        deliveryIncluded: form.deliveryIncluded !== false,
        discount: discountVal,
        shippingCharge: effectiveShipping,
        extraCharge: extraVal,
        codCharge: isCod ? effectiveCodCharge : 0,
        advancePaid: isCod ? advanceAmount : 0,
        codBalanceDue: isCod ? balanceDue : 0,
        paymentMethod: isCod
          ? "Cash on Delivery (COD)"
          : (form.notes?.trim() || (form.isCounterSale ? "In-Store / Direct" : "Prepaid")),
        paymentType: isCod ? "COD" : "Prepaid",
        subtotal: isGstOrder ? Number(taxableSubtotal.toFixed(2)) : grossItemsTotal,
        grossSubtotal: Number(grossItemsTotal.toFixed(2)),
        tax: isGstOrder ? roundedTax : 0,
        taxTotal: isGstOrder ? roundedTax : 0,
        items: items.map((it) => {
          const rate = isGstOrder ? Number(it.taxRate != null ? it.taxRate : (it.gstRate || 0)) : 0;
          const calc = calculateLineGst(it.price, it.quantity, rate, isGstInclusive);
          return {
            ...it,
            hsn: it.hsn || it.hsnCode || "4202",
            hsnCode: it.hsn || it.hsnCode || "4202",
            taxRate: rate,
            gstRate: rate,
            priceIncludesGst: isGstOrder && isGstInclusive,
            baseUnitPrice: calc.baseUnitPrice,
            lineBase: calc.lineBase,
            taxAmount: calc.taxAmount,
            total: calc.lineTotal,
          };
        }),
        total: grandTotal,
        userId: null,
      };

      const savedOrder = await createOrder(order);

      // Decrement stock for Master SKU items in inventory
      for (const it of items) {
        if (it.isMasterSku || it.sku) {
          try {
            await updateMasterSkuStock(it.id || it.sku, -Number(it.quantity || 1));
          } catch (e) {
            console.warn("Failed to update master SKU stock:", e);
          }
        }
      }

      toast.success("Manual order & invoice recorded! Master SKU inventory updated.");

      if (sendWhatsApp && form.phone.trim()) {
        const waUrl = buildWhatsAppOrderUrl({
          order: { ...order, id: savedOrder?.id },
          orderId: savedOrder?.id,
        });
        window.open(waUrl, "_blank");
      }

      onSaved();
    } catch (err) {
      toast.error(err.message || "Failed to save offline order");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-foreground/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-background w-full max-w-4xl max-h-[92vh] overflow-y-auto border border-border shadow-2xl rounded-lg my-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border sticky top-0 bg-background z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-500/10 text-amber-700 dark:text-amber-400 flex items-center justify-center">
              <Store className="w-5 h-5" />
            </div>
            <div>
              <div className="overline text-terracotta text-xs">Admin Sales</div>
              <h2 className="font-serif text-2xl">Add Offline / Store Order</h2>
            </div>
          </div>
          <button data-testid="offline-modal-close" onClick={onClose} className="p-2 text-muted-foreground hover:text-foreground">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-6">
          {/* Sale Type, Invoice Type & GST Mode Bar */}
          <div className="flex flex-col gap-3 bg-muted/40 p-3.5 rounded-lg border border-border">
            <div className="flex flex-wrap items-center justify-between gap-3">
              {/* Sale Type Selector */}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  data-testid="off-sale-counter"
                  onClick={() => toggleCounterSale(true)}
                  className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition cursor-pointer ${
                    form.isCounterSale ? "bg-terracotta text-primary-foreground shadow-xs font-semibold" : "bg-card border border-border text-foreground hover:bg-muted"
                  }`}
                >
                  In-Store / Counter Sale (Bolpur)
                </button>
                <button
                  type="button"
                  data-testid="off-sale-delivery"
                  onClick={() => toggleCounterSale(false)}
                  className={`px-3.5 py-1.5 rounded-md text-xs font-medium transition cursor-pointer ${
                    !form.isCounterSale ? "bg-terracotta text-primary-foreground shadow-xs font-semibold" : "bg-card border border-border text-foreground hover:bg-muted"
                  }`}
                >
                  Custom Address / Delivery
                </button>
              </div>

              {/* GST vs Non-GST Order Toggle */}
              <div className="flex items-center gap-1 bg-background p-1 rounded-lg border border-border text-xs">
                <button
                  type="button"
                  data-testid="off-type-gst"
                  onClick={() => handleToggleGstOrder(true)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-medium transition cursor-pointer ${
                    isGstOrder
                      ? "bg-terracotta text-white shadow-xs font-bold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Check className={`w-3.5 h-3.5 ${isGstOrder ? "opacity-100" : "opacity-0"}`} />
                  🧾 GST Invoice
                </button>
                <button
                  type="button"
                  data-testid="off-type-nongst"
                  onClick={() => handleToggleGstOrder(false)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-medium transition cursor-pointer ${
                    !isGstOrder
                      ? "bg-neutral-800 text-white shadow-xs font-bold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Check className={`w-3.5 h-3.5 ${!isGstOrder ? "opacity-100" : "opacity-0"}`} />
                  📄 Non-GST Invoice
                </button>
              </div>
            </div>

            {/* If GST Order: GST Mode & Breakdown Toggles */}
            {isGstOrder ? (
              <div className="flex flex-wrap items-center justify-between gap-3 pt-2 border-t border-border/60">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="flex items-center gap-1 bg-background p-1 rounded-md border border-border text-xs">
                    <span className="text-[11px] font-semibold text-muted-foreground px-1.5 hidden sm:inline">
                      GST Calculation:
                    </span>
                    <button
                      type="button"
                      data-testid="off-toggle-gst-inclusive"
                      onClick={() => handleGstModeChange("inclusive")}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded font-medium transition cursor-pointer ${
                        isGstInclusive
                          ? "bg-emerald-700 text-white shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      title="Entered rates already include GST (Retail MRP / Net Payable)"
                    >
                      <Check className={`w-3.5 h-3.5 ${isGstInclusive ? "opacity-100" : "opacity-0"}`} />
                      Include GST
                    </button>
                    <button
                      type="button"
                      data-testid="off-toggle-gst-exclusive"
                      onClick={() => handleGstModeChange("exclusive")}
                      className={`flex items-center gap-1 px-3 py-1.5 rounded font-medium transition cursor-pointer ${
                        !isGstInclusive
                          ? "bg-blue-700 text-white shadow-xs font-semibold"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                      title="Entered rates are base taxable prices; GST % is calculated and added on top"
                    >
                      <Check className={`w-3.5 h-3.5 ${!isGstInclusive ? "opacity-100" : "opacity-0"}`} />
                      Exclude GST (+Tax)
                    </button>
                  </div>

                  <span className={`px-2.5 py-1.5 rounded text-xs font-semibold border ${
                    isWestBengalState(form.state)
                      ? "bg-emerald-500/10 text-emerald-800 dark:text-emerald-300 border-emerald-500/30"
                      : "bg-blue-500/10 text-blue-800 dark:text-blue-300 border-blue-500/30"
                  }`}>
                    {isWestBengalState(form.state)
                      ? "🟢 Intra-State (West Bengal) → CGST 50% + SGST 50%"
                      : `🔵 Inter-State (${form.state || "Outside WB"}) → IGST 100%`}
                  </span>
                </div>
              </div>
            ) : (
              <div className="pt-2 border-t border-border/60 text-xs text-amber-800 dark:text-amber-300 flex items-center gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 text-amber-600" />
                <span>
                  <strong>Non-GST Mode:</strong> No taxes will be shown on the invoice, company GSTIN is omitted, and this order is <strong>excluded from GST tax filings &amp; reports</strong>.
                </span>
              </div>
            )}
          </div>

          {/* Customer Details */}
          <div className="grid sm:grid-cols-4 gap-4">
            <label className="block">
              <span className="block text-xs font-medium mb-1 text-muted-foreground">Customer Name *</span>
              <input
                data-testid="off-customer-name"
                required
                type="text"
                value={form.customerName}
                onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-medium mb-1 text-muted-foreground">Phone Number *</span>
              <input
                data-testid="off-phone"
                required
                type="text"
                placeholder="+91 9876543210"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
              />
            </label>

            <label className="block">
              <span className="block text-xs font-medium mb-1 text-muted-foreground">Email (optional)</span>
              <input
                data-testid="off-email"
                type="email"
                placeholder="customer@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
              />
            </label>

            {isGstOrder ? (
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-muted-foreground">Customer GSTIN (optional)</span>
                <input
                  data-testid="off-customer-gst"
                  type="text"
                  placeholder="e.g. 19ABCDE1234F1Z5"
                  value={form.customerGst || ""}
                  onChange={(e) => setForm({ ...form, customerGst: e.target.value.toUpperCase() })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground uppercase font-mono"
                />
              </label>
            ) : (
              <div className="flex items-center text-xs text-muted-foreground pt-6">
                <span>(Non-GST Order • Retail Bill)</span>
              </div>
            )}
          </div>

          {/* Address fields & State Selector */}
          <div className="space-y-2">
            <div className="grid sm:grid-cols-5 gap-3">
              <label className="block sm:col-span-2">
                <span className="block text-xs font-medium mb-1 text-muted-foreground">Address *</span>
                <input
                  data-testid="off-address"
                  required
                  type="text"
                  value={form.address}
                  onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-muted-foreground">City *</span>
                <input
                  data-testid="off-city"
                  required
                  type="text"
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                />
              </label>
              <label className="block">
                <span className="block text-xs font-semibold mb-1 text-foreground">
                  State (Place of Supply) *
                </span>
                <select
                  data-testid="off-state"
                  required
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-2.5 py-2 rounded-md text-sm outline-none focus:border-terracotta font-medium"
                >
                  {INDIAN_STATES.map((st) => (
                    <option key={st} value={st}>
                      {st} {isWestBengalState(st) ? "(WB • CGST+SGST)" : "(IGST)"}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-muted-foreground">Pincode *</span>
                <input
                  data-testid="off-pincode"
                  required
                  type="text"
                  value={form.pincode}
                  onChange={(e) => setForm({ ...form, pincode: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono"
                />
              </label>
            </div>
          </div>

          {/* Item Selector Box */}
          <div className="border border-border p-4 rounded-md bg-card space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border pb-3">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <ShoppingCart className="w-4 h-4 text-terracotta" /> Add Products / Items to Order
              </div>
              
              {/* Item Source Mode Switcher */}
              <div className="flex flex-wrap items-center gap-1.5 bg-muted p-1 rounded-md text-xs">
                <button
                  type="button"
                  data-testid="off-item-mode-sku"
                  onClick={() => setItemSourceMode("sku")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-medium transition cursor-pointer ${
                    itemSourceMode === "sku"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Boxes className="w-3.5 h-3.5 text-terracotta" /> Master SKU Catalog ({masterSkus.length})
                </button>
                <button
                  type="button"
                  data-testid="off-item-mode-catalog"
                  onClick={() => setItemSourceMode("catalog")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-medium transition cursor-pointer ${
                    itemSourceMode === "catalog"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Package className="w-3.5 h-3.5" /> Shop Products ({products.length})
                </button>
                <button
                  type="button"
                  data-testid="off-item-mode-manual"
                  onClick={() => setItemSourceMode("manual")}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded font-medium transition cursor-pointer ${
                    itemSourceMode === "manual"
                      ? "bg-background text-foreground shadow-xs font-semibold"
                      : "text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Edit3 className="w-3.5 h-3.5" /> Custom / Manual
                </button>
              </div>
            </div>

            {itemSourceMode === "sku" ? (
              <div className="space-y-3">
                <div className="flex flex-wrap items-center justify-between text-xs text-muted-foreground gap-2">
                  <span>Search or select from your central Master Product SKU list</span>
                  {currentMasterSku && (
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-foreground">SKU: {currentMasterSku.sku}</span>
                      <span>•</span>
                      <span>
                        Stock:{" "}
                        <strong
                          className={
                            Number(currentMasterSku.quantity || 0) === 0
                              ? "text-rose-600 font-bold"
                              : Number(currentMasterSku.quantity || 0) <= 3
                              ? "text-amber-600 font-bold"
                              : "text-emerald-700 dark:text-emerald-400 font-bold"
                          }
                        >
                          {currentMasterSku.quantity || 0} pcs
                        </strong>
                      </span>
                    </div>
                  )}
                </div>

                {/* SKU Search & Filter */}
                <div className="grid sm:grid-cols-12 gap-2.5 items-end">
                  <div className="sm:col-span-5 block">
                    <span className="block text-xs mb-1 font-medium text-foreground">
                      Search by SKU ID / Product Name
                    </span>
                    <div className="relative">
                      <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                      <input
                        type="text"
                        data-testid="off-sku-search-input"
                        value={skuSearch}
                        onChange={(e) => setSkuSearch(e.target.value)}
                        placeholder="Type SKU code (e.g. LC-BAG-001) or title..."
                        className="w-full bg-muted/50 border border-border pl-8 pr-7 py-2 rounded-md text-sm outline-none focus:border-terracotta"
                      />
                      {skuSearch && (
                        <button
                          type="button"
                          onClick={() => setSkuSearch("")}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-0.5"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="sm:col-span-7 block">
                    <span className="block text-xs mb-1 font-medium text-foreground">
                      Select Master SKU Dropdown ({filteredMasterSkus.length} available) *
                    </span>
                    <select
                      data-testid="off-select-master-sku"
                      value={selectedSkuId}
                      onChange={(e) => setSelectedSkuId(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta font-mono font-medium"
                    >
                      {filteredMasterSkus.length === 0 ? (
                        <option value="">No matching Master SKU found</option>
                      ) : (
                        filteredMasterSkus.map((s) => (
                          <option key={s.id} value={s.id}>
                            [{s.sku}] {s.name} • Stock: {s.quantity} pcs • ₹{s.saleCost}
                          </option>
                        ))
                      )}
                    </select>
                  </div>
                </div>

                {/* Selected SKU Attribute Strip */}
                {currentMasterSku && (
                  <div className="p-3 bg-muted/30 border border-border/80 rounded-md text-xs grid grid-cols-2 sm:grid-cols-4 gap-2">
                    <div>
                      <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Custom SKU ID</span>
                      <span className="font-mono font-bold text-terracotta">{currentMasterSku.sku}</span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Category &amp; Specs</span>
                      <span className="text-foreground truncate block">
                        {[currentMasterSku.category, currentMasterSku.material, currentMasterSku.colour]
                          .filter(Boolean)
                          .join(" • ") || "Standard"}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Cost / Potential Margin</span>
                      <span className="font-price text-foreground">
                        Cost: ₹{currentMasterSku.purchaseCost || 0}
                        {Number(currentMasterSku.saleCost || 0) > Number(currentMasterSku.purchaseCost || 0) && (
                          <span className="text-emerald-700 dark:text-emerald-400 font-semibold ml-1">
                            (+₹{Number(currentMasterSku.saleCost || 0) - Number(currentMasterSku.purchaseCost || 0)})
                          </span>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-[10px] uppercase text-muted-foreground block font-semibold">Available Stock</span>
                      <span
                        className={`font-bold ${
                          Number(currentMasterSku.quantity || 0) === 0
                            ? "text-rose-600"
                            : Number(currentMasterSku.quantity || 0) <= 3
                            ? "text-amber-600"
                            : "text-emerald-700 dark:text-emerald-400"
                        }`}
                      >
                        {currentMasterSku.quantity || 0} units
                      </span>
                    </div>
                  </div>
                )}

                {/* Pricing, Tax, Quantity & Add Button */}
                <div className="grid sm:grid-cols-12 gap-2.5 items-end">
                  <label className="sm:col-span-3 block">
                    <span className="block text-xs mb-1 text-muted-foreground">
                      {isGstInclusive ? "Sale Price (Incl. GST) ₹ *" : "Base Price (Excl. GST) ₹ *"}
                    </span>
                    <input
                      data-testid="off-sku-price"
                      type="number"
                      min="0"
                      value={skuPrice}
                      onChange={(e) => setSkuPrice(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta font-mono font-semibold"
                    />
                  </label>

                  <label className="sm:col-span-3 block">
                    <span className="block text-xs mb-1 text-muted-foreground">GST Tax % (default 0)</span>
                    <input
                      data-testid="off-sku-tax-rate"
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      placeholder="0"
                      value={skuTaxRate}
                      onChange={(e) => setSkuTaxRate(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta font-mono"
                    />
                  </label>

                  <label className="sm:col-span-2 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Qty *</span>
                    <input
                      data-testid="off-sku-qty"
                      type="number"
                      min="1"
                      value={skuQuantity}
                      onChange={(e) => setSkuQuantity(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-terracotta font-mono font-bold"
                    />
                  </label>

                  <div className="sm:col-span-2 block text-xs pb-1 font-price text-muted-foreground">
                    <div>Line Total:</div>
                    <strong className="text-foreground text-sm font-price">
                      ₹{skuPreview.lineTotal.toFixed(2)}
                    </strong>
                    {Number(skuTaxRate || 0) > 0 && (
                      <div className="text-[10px] text-muted-foreground leading-tight mt-0.5">
                        {isGstInclusive
                          ? `Taxable ₹${skuPreview.baseUnitPrice} + GST ₹${skuPreview.unitTax}`
                          : `Base ₹${skuPreview.baseUnitPrice} + GST ₹${(skuPreview.taxAmount / Math.max(1, Number(skuQuantity || 1))).toFixed(2)}`}
                      </div>
                    )}
                  </div>

                  <button
                    type="button"
                    data-testid="off-add-sku-item-btn"
                    onClick={addSkuItem}
                    disabled={!currentMasterSku}
                    className="sm:col-span-2 py-2 px-3 rounded-md bg-terracotta text-white hover:opacity-90 disabled:opacity-40 text-xs font-semibold flex items-center justify-center gap-1.5 cursor-pointer shadow-xs transition"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add to Invoice
                  </button>
                </div>
              </div>
            ) : itemSourceMode === "catalog" ? (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>Select an existing catalog product &amp; variant</span>
                  <span>Stock Available: <strong className="text-foreground">{selectedVariantStock}</strong></span>
                </div>
                <div className="grid sm:grid-cols-12 gap-2.5 items-end">
                  <label className="sm:col-span-3 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Select Product</span>
                    <select
                      data-testid="off-select-product"
                      value={productId}
                      onChange={(e) => setProductId(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                    >
                      {products.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.name} (₹{p.price})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="sm:col-span-2 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Variant / Tag</span>
                    <select
                      data-testid="off-select-variant"
                      value={variantTag}
                      onChange={(e) => setVariantTag(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                    >
                      {(currentProduct?.variants || []).map((v, i) => (
                        <option key={i} value={v.tag}>
                          {v.tag}{parseComboQty(v.comboQty) > 1 ? ` (${parseComboQty(v.comboQty)} Pcs Combo)` : ""} (Stock: {v.stock})
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="sm:col-span-2 block">
                    <span className="block text-xs mb-1 text-muted-foreground">
                      {isGstInclusive ? "Price (Incl. GST) ₹ *" : "Base Price (Excl. GST) ₹ *"}
                    </span>
                    <input
                      data-testid="off-unit-price"
                      type="number"
                      min="0"
                      value={unitPrice}
                      onChange={(e) => setUnitPrice(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono"
                    />
                  </label>

                  <label className="sm:col-span-2 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Tax % (default 0)</span>
                    <input
                      data-testid="off-tax-rate"
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      placeholder="0"
                      value={taxRate}
                      onChange={(e) => setTaxRate(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono"
                    />
                  </label>

                  <label className="sm:col-span-1 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Qty</span>
                    <input
                      data-testid="off-qty"
                      type="number"
                      min="1"
                      max={selectedVariantStock}
                      value={quantity}
                      onChange={(e) => setQuantity(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono"
                    />
                  </label>

                  <div className="sm:col-span-1 block text-xs pb-1 font-price text-muted-foreground">
                    <div>Line:</div>
                    <strong className="text-foreground text-xs font-price">
                      ₹{catalogPreview.lineTotal.toFixed(2)}
                    </strong>
                  </div>

                  <button
                    type="button"
                    data-testid="off-add-item-btn"
                    onClick={addItem}
                    disabled={selectedVariantStock <= 0}
                    className="sm:col-span-1 py-2 px-2 rounded-md bg-terracotta text-primary-foreground hover:opacity-90 disabled:opacity-40 text-xs font-medium flex items-center justify-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground flex items-center justify-between">
                  <span>Enter custom/unlisted item details (Name, Rate, Tax %, Quantity)</span>
                  <span className="font-semibold text-terracotta">
                    {isGstInclusive ? "Mode: Rates are GST-Inclusive" : "Mode: Rates are GST-Exclusive"}
                  </span>
                </div>
                <div className="grid sm:grid-cols-12 gap-2.5 items-end">
                  <label className="sm:col-span-3 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Custom Item Name / Title *</span>
                    <input
                      data-testid="off-manual-name"
                      type="text"
                      placeholder="e.g. Custom Leather Belt"
                      value={manualName}
                      onChange={(e) => setManualName(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                    />
                  </label>

                  <label className="sm:col-span-2 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Variant / Description</span>
                    <input
                      data-testid="off-manual-variant"
                      type="text"
                      placeholder="e.g. Custom / Brown L"
                      value={manualVariant}
                      onChange={(e) => setManualVariant(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                    />
                  </label>

                  <label className="sm:col-span-1 block">
                    <span className="block text-xs mb-1 text-muted-foreground">HSN *</span>
                    <input
                      data-testid="off-manual-hsn"
                      type="text"
                      placeholder="4202"
                      value={manualHsn}
                      onChange={(e) => setManualHsn(e.target.value.trim())}
                      className="w-full bg-muted/50 border border-border px-2 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono text-center"
                    />
                  </label>

                  <label className="sm:col-span-2 block">
                    <span className="block text-xs mb-1 text-muted-foreground">
                      {isGstOrder ? (isGstInclusive ? "Rate (Incl. GST) ₹ *" : "Base Rate (Excl. GST) ₹ *") : "Rate ₹ *"}
                    </span>
                    <input
                      data-testid="off-manual-price"
                      type="number"
                      min="0"
                      value={manualPrice}
                      onChange={(e) => setManualPrice(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono font-semibold"
                    />
                  </label>

                  <label className="sm:col-span-1 block">
                    <span className="block text-xs mb-1 text-muted-foreground">GST %</span>
                    <input
                      data-testid="off-manual-tax-rate"
                      type="number"
                      min="0"
                      max="100"
                      step="any"
                      disabled={!isGstOrder}
                      placeholder={isGstOrder ? "0" : "N/A"}
                      value={isGstOrder ? manualTaxRate : "0"}
                      onChange={(e) => setManualTaxRate(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-2 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono disabled:opacity-40"
                    />
                  </label>

                  <label className="sm:col-span-1 block">
                    <span className="block text-xs mb-1 text-muted-foreground">Qty *</span>
                    <input
                      data-testid="off-manual-qty"
                      type="number"
                      min="1"
                      value={manualQuantity}
                      onChange={(e) => setManualQuantity(e.target.value)}
                      className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono font-bold"
                    />
                  </label>

                  <div className="sm:col-span-1 block text-xs pb-1 font-price text-muted-foreground">
                    <div>Line:</div>
                    <strong className="text-foreground text-xs font-price">
                      ₹{manualPreview.lineTotal.toFixed(2)}
                    </strong>
                  </div>

                  <button
                    type="button"
                    data-testid="off-add-manual-item-btn"
                    onClick={addManualItem}
                    className="sm:col-span-1 py-2 px-2 rounded-md bg-terracotta text-primary-foreground hover:opacity-90 text-xs font-medium flex items-center justify-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" /> Add
                  </button>
                </div>
              </div>
            )}

            {/* Selected Items List */}
            {items.length > 0 ? (
              <div className="border border-border rounded-md overflow-hidden text-xs mt-3">
                <table className="w-full">
                  <thead className="bg-muted">
                    <tr className="text-left text-muted-foreground border-b border-border">
                      <th className="px-3 py-2">Item</th>
                      <th className="px-2 py-2 text-center">HSN</th>
                      <th className="px-3 py-2">Variant</th>
                      <th className="px-3 py-2 text-right">
                        Rate (₹)
                        <span className="block text-[9px] font-normal text-muted-foreground">
                          {isGstOrder ? (isGstInclusive ? "(Incl. GST)" : "(Excl. GST)") : "(Net)"}
                        </span>
                      </th>
                      {isGstOrder && (
                        <>
                          <th className="px-3 py-2 text-right">Taxable Base</th>
                          <th className="px-3 py-2 text-center">Tax %</th>
                          <th className="px-3 py-2 text-right">Tax (₹)</th>
                        </>
                      )}
                      <th className="px-3 py-2 text-center">Qty</th>
                      <th className="px-3 py-2 text-right">Total</th>
                      <th className="px-3 py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, idx) => {
                      const itemRate = Number(it.price || 0);
                      const itemQty = Number(it.quantity || 1);
                      const itemTaxRate = isGstOrder ? Number(it.taxRate != null ? it.taxRate : (it.gstRate || 0)) : 0;
                      const calc = calculateLineGst(itemRate, itemQty, itemTaxRate, isGstInclusive);

                      return (
                        <tr key={idx} className="border-b border-border/60 hover:bg-muted/20">
                          <td className="px-3 py-2 font-medium">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              {it.sku && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] font-mono bg-terracotta/10 text-terracotta border border-terracotta/20 font-bold" title="Master SKU ID">
                                  {it.sku}
                                </span>
                              )}
                              <span>{it.name}</span>
                              {(it.isManual || String(it.id).startsWith("manual_")) && (
                                <span className="px-1.5 py-0.5 rounded text-[10px] bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-500/20 font-semibold">
                                  Custom
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-2 py-2 text-center font-mono text-[10.5px] text-muted-foreground font-semibold">
                            {it.hsn || it.hsnCode || "4202"}
                          </td>
                          <td className="px-3 py-2 text-muted-foreground">{it.variantTag}</td>
                          <td className="px-3 py-2 text-right font-price font-medium">
                            <input
                              type="number"
                              min="0"
                              value={itemRate}
                              onChange={(e) => updateItemPrice(idx, e.target.value)}
                              className="w-16 bg-background border border-border px-1.5 py-0.5 rounded text-right text-xs font-price"
                              title="Edit Unit Rate"
                            />
                          </td>
                          {isGstOrder && (
                            <>
                              <td className="px-3 py-2 text-right font-price text-muted-foreground">
                                ₹{calc.lineBase.toFixed(2)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="inline-flex items-center justify-center gap-1">
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="any"
                                    value={itemTaxRate}
                                    onChange={(e) => updateItemTaxRate(idx, e.target.value)}
                                    className="w-12 bg-background border border-border px-1.5 py-0.5 rounded text-center text-xs font-mono"
                                    title="Edit Tax %"
                                  />
                                  <span className="text-muted-foreground text-[10px]">%</span>
                                </div>
                              </td>
                              <td className="px-3 py-2 text-right font-price font-medium text-emerald-700 dark:text-emerald-400">
                                {calc.taxAmount > 0 ? `₹${calc.taxAmount.toFixed(2)}` : "₹0"}
                              </td>
                            </>
                          )}
                          <td className="px-3 py-2 text-center">
                            <input
                              type="number"
                              min="1"
                              value={itemQty}
                              onChange={(e) => updateItemQty(idx, e.target.value)}
                              className="w-12 bg-background border border-border px-2 py-0.5 rounded text-center text-xs font-price font-semibold"
                            />
                          </td>
                          <td className="px-3 py-2 text-right font-price font-bold">₹{calc.lineTotal.toFixed(2)}</td>
                          <td className="px-3 py-2 text-right">
                            <button
                              type="button"
                              onClick={() => removeItem(idx)}
                              className="text-muted-foreground hover:text-destructive p-1 cursor-pointer"
                              title="Remove item"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-6 text-xs text-muted-foreground border border-dashed border-border rounded-md">
                No items added yet. Choose items from catalog or enter manually above and click "Add".
              </div>
            )}
          </div>

          {/* Payment Method & Terms */}
          <div className="bg-card border border-border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                <CreditCard className="w-3.5 h-3.5 text-terracotta" /> Payment Method & COD Details
              </span>
              {paymentMode === "cod" && (
                <span className="text-xs font-semibold px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-300">
                  COD Handling Fee: +₹{effectiveCodCharge}
                </span>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {/* Prepaid / In-Store */}
              <button
                type="button"
                data-testid="off-payment-prepaid"
                onClick={() => setPaymentMode("prepaid")}
                className={`p-3 rounded-lg border text-left transition flex items-start gap-3 cursor-pointer ${
                  paymentMode === "prepaid"
                    ? "border-terracotta bg-terracotta/5 shadow-xs"
                    : "border-border hover:border-muted-foreground/40 bg-muted/20"
                }`}
              >
                <div className={`mt-0.5 p-1 rounded-full ${paymentMode === "prepaid" ? "bg-terracotta text-white" : "bg-muted text-muted-foreground"}`}>
                  <CreditCard className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-xs sm:text-sm text-foreground">
                      Prepaid / Direct / In-Store
                    </span>
                    <span className="text-[10px] uppercase font-bold text-emerald-700 dark:text-emerald-400">
                      ₹0 Extra
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Full payment received via Cash, UPI, Card, or Bank transfer. No COD logistics fee.
                  </div>
                </div>
              </button>

              {/* Cash on Delivery (COD) */}
              <button
                type="button"
                data-testid="off-payment-cod"
                onClick={() => setPaymentMode("cod")}
                className={`p-3 rounded-lg border text-left transition flex items-start gap-3 cursor-pointer ${
                  paymentMode === "cod"
                    ? "border-terracotta bg-terracotta/5 shadow-xs"
                    : "border-border hover:border-muted-foreground/40 bg-muted/20"
                }`}
              >
                <div className={`mt-0.5 p-1 rounded-full ${paymentMode === "cod" ? "bg-terracotta text-white" : "bg-muted text-muted-foreground"}`}>
                  <Banknote className="w-3.5 h-3.5" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-xs sm:text-sm text-foreground">
                      Cash on Delivery (COD)
                    </span>
                    <span className="font-price font-bold text-xs text-amber-700 dark:text-amber-400">
                      +₹{effectiveCodCharge} Fee
                    </span>
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5">
                    Charge: ₹50 or 3% of order (whichever is higher).
                  </div>
                </div>
              </button>
            </div>

            {/* COD Details Breakdown & Manual Advance Input */}
            {paymentMode === "cod" && (
              <div className="p-3.5 bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200/70 dark:border-amber-800/40 rounded-lg text-xs space-y-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-amber-900 dark:text-amber-200">
                    <span className="font-semibold">COD Handling Cost:</span> ₹50 or 3% of total bill (₹{Math.round(totalBillBeforeCod)} × 3% = ₹{Math.round(totalBillBeforeCod * 0.03)}) → Applied:{" "}
                    <strong>₹{effectiveCodCharge}</strong> (Higher of ₹50 or 3%).
                  </div>
                  <label className="flex items-center gap-1.5 text-muted-foreground text-[11px] cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid="off-custom-cod-checkbox"
                      checked={isCustomCod}
                      onChange={(e) => {
                        setIsCustomCod(e.target.checked);
                        if (!e.target.checked) setCustomCodCharge("");
                        else setCustomCodCharge(String(autoCodCharge));
                      }}
                      className="w-3.5 h-3.5 accent-terracotta rounded cursor-pointer"
                    />
                    <span>Custom COD Fee</span>
                  </label>
                </div>

                {isCustomCod && (
                  <div className="flex items-center gap-2 pt-1 border-t border-amber-200/50 dark:border-amber-800/30">
                    <span className="text-[11px] text-muted-foreground">Adjust COD Fee (₹):</span>
                    <input
                      type="number"
                      min="0"
                      data-testid="off-custom-cod-input"
                      value={customCodCharge}
                      onChange={(e) => setCustomCodCharge(e.target.value)}
                      placeholder={String(autoCodCharge)}
                      className="w-24 bg-background border border-border px-2 py-1 rounded text-xs font-mono font-bold"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setCustomCodCharge(String(autoCodCharge));
                        setIsCustomCod(false);
                      }}
                      className="text-[11px] text-terracotta hover:underline cursor-pointer"
                    >
                      Reset to rule (₹{autoCodCharge})
                    </button>
                  </div>
                )}

                {/* Advance Amount Manual Input & Collect on Delivery Card */}
                <div className="pt-2 border-t border-amber-200/70 dark:border-amber-800/40 grid sm:grid-cols-2 gap-3 items-center">
                  <div>
                    <label className="block">
                      <span className="block text-xs font-semibold mb-1 text-foreground">
                        Advance Amount Received (₹) * <span className="text-[10px] text-terracotta font-normal">(Manually filled)</span>
                      </span>
                      <input
                        type="number"
                        min="0"
                        max={grandTotal}
                        data-testid="off-cod-advance-paid"
                        value={codAdvancePaid}
                        onChange={(e) => setCodAdvancePaid(e.target.value)}
                        placeholder="0"
                        className="w-full bg-background border border-border px-3 py-1.5 rounded-md text-sm font-mono font-bold outline-none focus:border-terracotta"
                      />
                    </label>
                    <span className="text-[10px] text-muted-foreground mt-0.5 block">
                      Enter advance amount collected upfront from the customer (e.g. 0, 100, 200).
                    </span>
                  </div>

                  <div className="bg-amber-100/90 dark:bg-amber-900/40 p-2.5 rounded-lg border border-amber-300/60 dark:border-amber-700/50 space-y-1">
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">Total Bill (Pre-COD):</span>
                      <span className="font-price font-semibold">₹{totalBillBeforeCod}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">+ COD Handling Cost:</span>
                      <span className="font-price font-semibold text-amber-800 dark:text-amber-300">+₹{effectiveCodCharge}</span>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">− Prepaid Advance:</span>
                      <span className="font-price font-semibold text-emerald-800 dark:text-emerald-400">−₹{advanceAmount}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-bold pt-1 border-t border-amber-300/60 dark:border-amber-700/50">
                      <span className="text-terracotta uppercase">Collect on Delivery:</span>
                      <span className="font-price text-sm text-terracotta font-extrabold">₹{balanceDue}</span>
                    </div>
                  </div>
                </div>

                <div className="pt-2 border-t border-amber-200/70 dark:border-amber-800/40 text-[11px] text-amber-900/90 dark:text-amber-200/90">
                  <strong>Calculation:</strong> Total Bill (₹{totalBillBeforeCod}) + COD Handling (₹{effectiveCodCharge}) − Advance (₹{advanceAmount}) = <strong>₹{balanceDue} to collect on delivery</strong>.
                </div>
              </div>
            )}
          </div>

          {/* Order Status & Discount & Notes */}
          <div className="space-y-3">
            <label className="flex items-center gap-2 py-1.5 px-2.5 rounded bg-muted/40 border border-border/80 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="off-delivery-included"
                checked={form.deliveryIncluded !== false}
                onChange={(e) => setForm({ ...form, deliveryIncluded: e.target.checked })}
                className="w-4 h-4 accent-terracotta rounded cursor-pointer"
              />
              <span className="text-xs font-semibold text-foreground">
                Delivery Included in Price (Free Shipping on Invoice)
              </span>
              {form.deliveryIncluded === false && (
                <span className="text-[11px] text-amber-700 dark:text-amber-400 font-medium ml-auto">
                  (Shipping not free — will be calculated later)
                </span>
              )}
            </label>

            <div className="grid sm:grid-cols-4 gap-4">
              <label className="block">
                <span className="block text-xs font-medium mb-1 text-muted-foreground">Order Status</span>
                <select
                  data-testid="off-status"
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                >
                  <option value="New">New</option>
                  <option value="Delivered">Delivered (Completed Sale)</option>
                  <option value="Confirmed">Confirmed</option>
                  <option value="Shipped">Shipped</option>
                </select>
              </label>

              {form.deliveryIncluded === false ? (
                <label className="block">
                  <span className="block text-xs font-medium mb-1 text-muted-foreground">
                    Shipping Fee (₹) <span className="text-[10px] text-amber-600 font-normal">(Leave 0 to calculate later)</span>
                  </span>
                  <input
                    data-testid="off-shipping-fee"
                    type="number"
                    min="0"
                    placeholder="0 (Calculate later)"
                    value={form.shippingCharge}
                    onChange={(e) => setForm({ ...form, shippingCharge: e.target.value })}
                    className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono"
                  />
                </label>
              ) : null}

              <label className="block">
                <span className="block text-xs font-medium mb-1 text-muted-foreground">Discount (₹)</span>
                <input
                  data-testid="off-discount"
                  type="number"
                  min="0"
                  value={form.discount}
                  onChange={(e) => setForm({ ...form, discount: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground font-mono"
                />
              </label>

              <label className={`block ${form.deliveryIncluded === false ? "sm:col-span-1" : "sm:col-span-2"}`}>
                <span className="block text-xs font-medium mb-1 text-muted-foreground">Notes / Reference</span>
                <input
                  data-testid="off-notes"
                  type="text"
                  placeholder="e.g. Paid via UPI, Cash, Wholesale"
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full bg-muted/50 border border-border px-3 py-2 rounded-md text-sm outline-none focus:border-foreground"
                />
              </label>
            </div>
          </div>

          {/* Summary & Actions */}
          <div className="border-t border-border pt-4 flex flex-wrap items-center justify-between gap-4">
            <div className="space-y-1 text-sm">
              <div className="text-muted-foreground text-xs font-price font-medium flex flex-wrap items-center gap-x-3 gap-y-1">
                {isGstOrder ? (
                  <>
                    <span>Taxable Base: ₹{taxableSubtotal.toFixed(2)}</span>
                    <span className="text-emerald-700 dark:text-emerald-400 font-semibold">
                      GST ({isGstInclusive ? "Included" : "Added"}): ₹{roundedTax.toFixed(2)}
                    </span>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                      isGstInclusive
                        ? "bg-emerald-500/15 text-emerald-800 dark:text-emerald-300 border border-emerald-500/30"
                        : "bg-blue-500/15 text-blue-800 dark:text-blue-300 border border-blue-500/30"
                    }`}>
                      {isGstInclusive ? "GST-Inclusive" : "GST-Exclusive"}
                    </span>
                  </>
                ) : (
                  <>
                    <span>Items Subtotal: ₹{grossItemsTotal.toFixed(2)}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-neutral-200 dark:bg-neutral-800 text-foreground border border-border">
                      Non-GST (No Tax)
                    </span>
                  </>
                )}
                {effectiveShipping > 0 ? (
                  <span>Shipping: +₹{effectiveShipping}</span>
                ) : form.deliveryIncluded === false ? (
                  <span className="text-amber-700 dark:text-amber-400 font-semibold italic">
                    Shipping: Will be calculated later
                  </span>
                ) : (
                  <span className="text-emerald-700 dark:text-emerald-400">Shipping: Free (Included)</span>
                )}
                {discountVal > 0 && <span>Discount: −₹{discountVal}</span>}
                {paymentMode === "cod" && (
                  <>
                    <span className="text-amber-700 dark:text-amber-400 font-bold">
                      COD Fee: +₹{effectiveCodCharge}
                    </span>
                    <span className="text-emerald-700 dark:text-emerald-400 font-semibold">
                      Advance: ₹{advanceAmount}
                    </span>
                    <span className="text-amber-800 dark:text-amber-300 font-bold">
                      Collect on Delivery: ₹{balanceDue}
                    </span>
                  </>
                )}
              </div>
              <div className="text-xl font-medium text-foreground">
                Grand Total: <span className="font-price text-2xl font-bold text-terracotta">₹{grandTotal}</span>
                {paymentMode === "cod" && (
                  <span className="text-xs ml-2 font-normal text-amber-800 dark:text-amber-300 font-sans">
                    (₹{advanceAmount} Advance + ₹{balanceDue} on Delivery)
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col sm:flex-row items-end sm:items-center gap-3">
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
                <input
                  type="checkbox"
                  data-testid="off-send-whatsapp"
                  checked={sendWhatsApp}
                  onChange={(e) => setSendWhatsApp(e.target.checked)}
                  className="w-3.5 h-3.5 accent-terracotta rounded cursor-pointer"
                />
                <span className="flex items-center gap-1 text-foreground">
                  <Send className="w-3 h-3 text-emerald-600" /> Open WhatsApp Confirmation
                </span>
              </label>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-full border border-border text-sm hover:bg-muted cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  data-testid="off-save-order-btn"
                  disabled={busy || items.length === 0}
                  className="px-6 py-2.5 rounded-full bg-terracotta text-primary-foreground font-medium text-sm hover:opacity-90 disabled:opacity-40 shadow-sm transition cursor-pointer"
                >
                  {busy ? "Saving Order…" : "Complete & Save Order"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
